import { useTranslation } from 'react-i18next'
import { Linkedin, Twitter, Github, Mail } from 'lucide-react'

export default function Footer() {
  const { t } = useTranslation(['translation', 'home'])
  const currentYear = new Date().getFullYear()

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <footer className="bg-slate-100 dark:bg-slate-950 text-gray-900 dark:text-white border-t border-gray-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img
                src="/logo.svg"
                alt="Bassilichat Inc"
                className="h-10 w-10"
              />
              <h3 className="text-2xl font-bold bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600 bg-clip-text text-transparent">
                Bassilichat Inc
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md">
              {t('home:footer.description')}
            </p>
            <div className="flex space-x-4 mb-4">
              <a
                href="https://www.linkedin.com/company/bassilichat/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-6 h-6" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-6 h-6" />
              </a>
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-6 h-6" />
              </a>
              <a
                href="mailto:info@bassilichat.com"
                className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                aria-label="Email"
              >
                <Mail className="w-6 h-6" />
              </a>
            </div>
            <p className="text-gray-600 dark:text-gray-500 text-sm">
              📍 {t('home:footer.address')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t('home:footer.quickLinks')}</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => scrollToSection('#about')}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white transition-colors text-left"
                >
                  {t('nav.about', { ns: 'translation' })}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('#products')}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white transition-colors text-left"
                >
                  {t('nav.products', { ns: 'translation' })}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('#careers')}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white transition-colors text-left"
                >
                  {t('nav.careers', { ns: 'translation' })}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('#contact')}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white transition-colors text-left"
                >
                  {t('nav.contact', { ns: 'translation' })}
                </button>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t('home:footer.products')}</h4>
            <ul className="space-y-2">
              <li className="text-gray-600 dark:text-gray-400">{t('home:products.bassiliChat.title')}</li>
              <li className="text-gray-600 dark:text-gray-400">{t('home:products.bassiliTrade.title')}</li>
              <li className="text-gray-600 dark:text-gray-400">{t('home:products.bassiliTravel.title')}</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-300 dark:border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            &copy; {currentYear} Bassilichat Inc {t('home:footer.rights')}
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white text-sm transition-colors">
              {t('home:footer.privacy')}
            </button>
            <button className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white text-sm transition-colors">
              {t('home:footer.terms')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
